import { StackLayout } from '@euler/app/components/StackLayout';
import { IconProps } from '@euler/app/components/icons/types';
import { Typography } from '@euler/app/components/typography';
import { useVoiceFeatures } from '@euler/app/components/voice/useVoiceFeatures';
import {
  estimatedFinishTimeValue,
  requireReasonForEstimatedFinishTimeChange,
} from '@euler/app/flows/order/pages/OrderScreen/functions/estimateFinishTimeUtil';
import {
  DateTimePicker,
  useDateTimePicker,
} from '@euler/components/DateTimePicker';
import { Icon } from '@euler/components/Icomoon';
import { Input } from '@euler/components/form/Input';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { uniq } from '@euler/utils/array';
import { formatDateTime } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { memo, useCallback } from 'react';
import {
  Platform,
  StyleProp,
  TextStyle,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { Path, Svg } from 'react-native-svg';
import { useOrderFlowContext } from '../../../../OrderFlowContext';
import { Cell } from '../components/Cell';
import { IconWrapper } from '../components/IconWrapper';
import { Row } from '../components/Row';
import { useOrderFormStyles } from '../components/styles';

export const EstimatedFinishTimeRow = memo(
  ({ required, ...props }: { required?: boolean; disabled?: boolean }) => {
    const { rpx } = useResponsiveContext();
    const { isVoiceInputEnabled } = useVoiceFeatures();

    const {
      theme,
      formController: { form },
      mode,
      type,
      basicInfo,
      storeConfiguration,
    } = useOrderFlowContext();

    const { iconColor, placeholderColor } = theme;

    const [isDateTimePickerVisible, showDateTimePicker, hideDateTimePicker] =
      useDateTimePicker();

    const onConfirm = usePersistFn((value: Date) => {
      form.estimatedFinishTime.update(value);
      hideDateTimePicker();
    });

    // Fix 'invalid date format' error
    const value = form.estimatedFinishTime.value
      ? formatDateTime(form.estimatedFinishTime.value)
      : '';
    const styles = useOrderFormStyles();

    const originalValue = estimatedFinishTimeValue(
      basicInfo?.estimatedFinishTime,
    );
    const currentValue = estimatedFinishTimeValue(
      form.estimatedFinishTime.value,
    );
    const showReason =
      mode === 'update' &&
      requireReasonForEstimatedFinishTimeChange(currentValue, originalValue) &&
      storeConfiguration.requireReasonForUpdateOrderEstimatedFinishTime;

    return (
      <Row
        errorProp="estimatedFinishTime"
        detailIcon={'disclosure'}
        icon={
          <IconWrapper>
            <RowIcon color={iconColor} />
          </IconWrapper>
        }
        onPress={showDateTimePicker}
        {...props}
      >
        <Cell
          label={type === 'main' ? '预计交车时间' : '预计完成时间'}
          required={required}
        >
          <Input
            style={styles.formInput}
            placeholderTextColor={placeholderColor}
            value={value}
            placeholder={Platform.OS === 'web' ? 'YYYY/MM/DD' : '可选'}
            errorMsg={form.estimatedFinishTime.error}
            editable={false}
            pointerEvents={Platform.OS === 'web' ? undefined : 'none'}
            onChangeText={
              Platform.OS === 'web'
                ? form.estimatedFinishTime.update
                : undefined
            }
            onBlur={
              Platform.OS === 'web'
                ? form.estimatedFinishTime.onBlur
                : undefined
            }
          />
          <DateTimePicker
            isVisible={isDateTimePickerVisible}
            mode="datetime"
            date={
              form.estimatedFinishTime.value
                ? new Date(form.estimatedFinishTime.value)
                : undefined
            }
            onConfirm={onConfirm}
            onCancel={hideDateTimePicker}
            confirmTextIOS="确认"
            cancelTextIOS="关闭"
          />
          {showReason && (
            <TouchableOpacity
              activeOpacity={1}
              css={`
                margin-top: ${rpx(10)}px;
                width: 100%;
                align-items: stretch;
              `}
            >
              <StackLayout direction="row" alignItems="center" gap={rpx(2)}>
                <Typography.Text>修改原因: </Typography.Text>
                <UpdateReasonMenu
                  reasons={
                    storeConfiguration.predefinedUpdateOrderEstimatedFinishTimeReasons
                  }
                  value={form.estimatedFinishTimeUpdateReason.value}
                  onChange={reason => {
                    form.estimatedFinishTimeUpdateReason.update(reason);
                    form.estimatedFinishTime.validate();
                  }}
                />
              </StackLayout>
              <StackLayout
                direction="row"
                alignItems="center"
                gap={rpx(2)}
                css={`
                  margin-top: ${rpx(2)}px;
                  width: 100%;
                `}
              >
                <Typography.Text>修改备注: </Typography.Text>
                <Input
                  style={[styles.formInput, { fontSize: rpx(14) }]}
                  containerStyle={{ flex: 1 }}
                  value={form.estimatedFinishTimeUpdateRemark.value}
                  placeholder="输入修改备注信息 (可选)"
                  onChangeText={form.estimatedFinishTimeUpdateRemark.update}
                  withVoiceInput={isVoiceInputEnabled}
                />
              </StackLayout>
            </TouchableOpacity>
          )}
        </Cell>
      </Row>
    );
  },
);

const UpdateReasonMenu = memo(
  ({
    reasons,
    value,
    placeholder,
    style,
    textStyle,
    placeholderStyle,
    placeholderTextColor = '#ccc',
    onChange,
  }: {
    reasons: string[] | undefined;
    value: string | undefined;
    placeholder?: string;
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
    placeholderStyle?: StyleProp<TextStyle>;
    placeholderTextColor?: string;
    onChange?: (value: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { showActionSheetWithOptions } = useActionSheet();
    const onPress = useCallback(() => {
      const items = uniq(reasons?.filter(x => x.trim()) ?? ['增项', '其他']);
      showActionSheetWithOptions(
        {
          title: '选择修改预计完工交车时间的原因',
          options: items.concat(['关闭']),
          cancelButtonIndex: items.length,
        },
        buttonIndex => {
          if (buttonIndex == null || buttonIndex === items.length) {
            return;
          }
          onChange?.(items[buttonIndex]);
        },
      );
    }, [onChange, reasons, showActionSheetWithOptions]);

    return (
      <TouchableOpacity
        style={style}
        onPress={onPress}
        css={`
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
        `}
      >
        <Typography.Label
          size={rpx(14)}
          family={'medium'}
          style={[textStyle, value == null ? placeholderStyle : undefined]}
          color={value == null ? placeholderTextColor : undefined}
        >
          {value ?? placeholder ?? '请选择原因'}
        </Typography.Label>
        <Icon name="chevron-down" color="#999" size={rpx(20)} />
      </TouchableOpacity>
    );
  },
);

const RowIcon = memo(({ size, color = '#07F', style, ...props }: IconProps) => {
  const { rpx } = useResponsiveContext();
  return (
    <Svg
      width={size ?? rpx(22)}
      height={size ?? rpx(21)}
      fill="none"
      viewBox="0 0 22 21"
      style={style}
      {...props}
    >
      <Path
        d="M5.57 18.094H2.784c-.975 0-1.393-.629-1.393-1.392V2.785c0-1.236.24-1.393 1.393-1.393h11.138c1.077 0 1.412.361 1.412 1.393v1.863c0 .972 1.393 1.098 1.393 0V2.785C16.728.73 16.064 0 13.92 0H2.785C1.419 0 0 .167 0 2.723v14.431c-.003.275.036.55.116.813a2.192 2.192 0 0 0 2.143 1.525c1.703.007 2.395 0 3.308 0 .913 0 .852-1.398.002-1.398Z"
        fill={color}
      />
      <Path
        d="M4.183 4.167c-1 0-1.044 1.41 0 1.41h8.354c.957 0 1.109-1.41 0-1.41H4.183ZM6.426 8.34H4.183c-1 0-1.044 1.411 0 1.411h2.243c.957 0 1.109-1.41 0-1.41ZM15.34 7.6l.016 5.713h5.645A5.617 5.617 0 0 0 15.48 7.6h-.141Z"
        fill={color}
      />
      <Path
        d="M14.297 8.73a6.046 6.046 0 0 0-5.994 5.864 5.77 5.77 0 0 0 5.77 5.77 6.044 6.044 0 0 0 5.864-5.993h-5.64V8.73Z"
        fill={color}
      />
    </Svg>
  );
});
